import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import FilterPanel from './components/FilterPanel';
import SortControls from './components/SortControls';
import ServiceGrid from './components/ServiceGrid';
import RFQModal from './components/RFQModal';

const ProductCatalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedService, setSelectedService] = useState(null);
  const [isRFQModalOpen, setIsRFQModalOpen] = useState(false);

  // Mock user data
  const mockUser = {
    id: 1,
    name: "John Doe",
    email: "john.doe@company.com",
    role: "customer"
  };

  // Filter state
  const [filters, setFilters] = useState({
    search: searchParams?.get('search') || '',
    category: searchParams?.get('category') || 'all',
    priceRange: {
      min: searchParams?.get('minPrice') || '',
      max: searchParams?.get('maxPrice') || ''
    },
    timeline: searchParams?.get('timeline') || 'all',
    serviceTypes: searchParams?.get('types')?.split(',') || []
  });

  // Sort state
  const [sortBy, setSortBy] = useState(searchParams?.get('sortBy') || 'relevance');
  const [sortOrder, setSortOrder] = useState(searchParams?.get('sortOrder') || 'desc');

  // Mock services data
  const mockServices = [
    {
      id: 1,
      title: "IT Infrastructure Assessment",
      category: "Consultancy",
      description: `Comprehensive evaluation of your current IT infrastructure to identify gaps, security vulnerabilities, and optimization opportunities.\nOur expert consultants will analyze your systems and provide actionable recommendations.`,
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=500",
      priceRange: "UGX 2,500,000 - 5,000,000",
      timeline: "2-3 weeks",
      teamSize: "2-3 experts",
      rating: 4.8,
      reviews: 24,
      isPopular: true,
      isNew: false,
      serviceTypes: ['consultation', 'implementation']
    },
    {
      id: 2,
      title: "Network Infrastructure Setup",
      category: "Infrastructure",
      description: `Complete network infrastructure design and implementation including servers, switches, routers, and security appliances.\nWe ensure scalable and secure network architecture for your business growth.`,
      image: "https://images.pexels.com/photos/442150/pexels-photo-442150.jpeg?w=500",
      priceRange: "UGX 15,000,000 - 50,000,000",
      timeline: "1-2 months",
      teamSize: "4-6 engineers",
      rating: 4.9,
      reviews: 18,
      isPopular: false,
      isNew: true,
      serviceTypes: ['implementation', 'maintenance']
    },
    {
      id: 3,
      title: "Cybersecurity Solutions",
      category: "Networking",
      description: `Advanced cybersecurity implementation including firewalls, intrusion detection, endpoint protection, and security monitoring.\nProtect your business from evolving cyber threats with enterprise-grade security.`,
      image: "https://images.pixabay.com/photo/2018/05/14/16/54/cyber-3400789_1280.jpg?w=500",
      priceRange: "UGX 8,000,000 - 25,000,000",
      timeline: "3-4 weeks",
      teamSize: "3-4 specialists",
      rating: 4.7,
      reviews: 31,
      isPopular: true,
      isNew: false,
      serviceTypes: ['implementation', 'maintenance', 'training']
    },
    {
      id: 4,
      title: "Custom Software Development",
      category: "Software",
      description: `Bespoke software solutions tailored to your business needs including web applications, mobile apps, and enterprise systems.\nFrom concept to deployment with ongoing support and maintenance.`,
      image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=500",
      priceRange: "UGX 10,000,000 - 100,000,000",
      timeline: "2-6 months",
      teamSize: "3-8 developers",
      rating: 4.6,
      reviews: 42,
      isPopular: false,
      isNew: false,
      serviceTypes: ['consultation', 'implementation', 'training']
    },
    {
      id: 5,
      title: "IT Policy Development",
      category: "Policy",
      description: `Comprehensive IT governance and policy framework development including security policies, data management, and compliance procedures.\nEnsure regulatory compliance and operational excellence.`,
      image: "https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?w=500",
      priceRange: "UGX 3,000,000 - 8,000,000",
      timeline: "3-4 weeks",
      teamSize: "2-3 consultants",
      rating: 4.5,
      reviews: 16,
      isPopular: false,
      isNew: true,
      serviceTypes: ['consultation']
    },
    {
      id: 6,
      title: "Digital Transformation Strategy",
      category: "Strategy",
      description: `Strategic roadmap for digital transformation including technology assessment, change management, and implementation planning.\nTransform your business operations with cutting-edge digital solutions.`,
      image: "https://images.pixabay.com/photo/2017/05/10/13/40/robot-2301646_1280.jpg?w=500",
      priceRange: "UGX 5,000,000 - 15,000,000",
      timeline: "1-2 months",
      teamSize: "3-5 strategists",
      rating: 4.8,
      reviews: 28,
      isPopular: true,
      isNew: false,
      serviceTypes: ['consultation', 'training']
    }
  ];

  // Filter and sort services
  const filteredAndSortedServices = useMemo(() => {
    let filtered = mockServices?.filter(service => {
      // Search filter
      if (filters?.search) {
        const searchTerm = filters?.search?.toLowerCase();
        if (!service?.title?.toLowerCase()?.includes(searchTerm) &&
            !service?.description?.toLowerCase()?.includes(searchTerm) &&
            !service?.category?.toLowerCase()?.includes(searchTerm)) {
          return false;
        }
      }

      // Category filter
      if (filters?.category !== 'all' && service?.category?.toLowerCase() !== filters?.category) {
        return false;
      }

      // Timeline filter
      if (filters?.timeline !== 'all' && service?.timeline !== filters?.timeline) {
        return false;
      }

      // Service types filter
      if (filters?.serviceTypes?.length > 0) {
        const hasMatchingType = filters?.serviceTypes?.some(type => 
          service?.serviceTypes?.includes(type)
        );
        if (!hasMatchingType) return false;
      }

      // Price range filter (simplified - in real app would parse price ranges)
      // This is a basic implementation for demonstration

      return true;
    });

    // Sort services
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          // Simplified price comparison
          const aPrice = parseInt(a?.priceRange?.match(/\d+/)?.[0]);
          const bPrice = parseInt(b?.priceRange?.match(/\d+/)?.[0]);
          return sortOrder === 'asc' ? aPrice - bPrice : bPrice - aPrice;
        case 'rating':
          return sortOrder === 'asc' ? a?.rating - b?.rating : b?.rating - a?.rating;
        case 'popularity':
          return b?.reviews - a?.reviews;
        case 'newest':
          return b?.isNew - a?.isNew;
        default:
          return 0;
      }
    });

    return filtered;
  }, [filters, sortBy, sortOrder]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (filters?.search) params?.set('search', filters?.search);
    if (filters?.category !== 'all') params?.set('category', filters?.category);
    if (filters?.timeline !== 'all') params?.set('timeline', filters?.timeline);
    if (filters?.serviceTypes?.length > 0) params?.set('types', filters?.serviceTypes?.join(','));
    if (filters?.priceRange?.min) params?.set('minPrice', filters?.priceRange?.min);
    if (filters?.priceRange?.max) params?.set('maxPrice', filters?.priceRange?.max);
    if (sortBy !== 'relevance') params?.set('sortBy', sortBy);
    if (sortOrder !== 'desc') params?.set('sortOrder', sortOrder);

    setSearchParams(params);
  }, [filters, sortBy, sortOrder, setSearchParams]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: 'all',
      priceRange: { min: '', max: '' },
      timeline: 'all',
      serviceTypes: []
    });
  };

  const handleSortChange = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
  };

  const handleRequestQuote = (service) => {
    setSelectedService(service);
    setIsRFQModalOpen(true);
  };

  const handleToggleFilters = () => {
    setIsFiltersCollapsed(!isFiltersCollapsed);
  };

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation user={mockUser} rfqCount={3} />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbNavigation />
          
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              ICT Solutions Catalog
            </h1>
            <p className="text-lg text-muted-foreground">
              Discover comprehensive technology solutions tailored for Uganda's business community
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <div className="lg:w-80 lg:flex-shrink-0">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                isCollapsed={isFiltersCollapsed}
                onToggleCollapse={handleToggleFilters}
              />
            </div>

            {/* Main Content */}
            <div className="flex-1">
              <SortControls
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                resultsCount={filteredAndSortedServices?.length}
                onToggleFilters={handleToggleFilters}
              />

              <ServiceGrid
                services={filteredAndSortedServices}
                viewMode={viewMode}
                onRequestQuote={handleRequestQuote}
              />
            </div>
          </div>
        </div>
      </main>
      {/* RFQ Modal */}
      <RFQModal
        isOpen={isRFQModalOpen}
        onClose={() => setIsRFQModalOpen(false)}
        service={selectedService}
      />
    </div>
  );
};

export default ProductCatalog;